import { useState } from 'react';
import type { CreateAccountData } from '../../api/accounts';

interface AccountFormProps {
  onSubmit: (data: CreateAccountData) => Promise<void>;
  onCancel: () => void;
}

const REGIONS = [
  'us-east-1',
  'us-west-2',
  'eu-west-1',
  'eu-central-1',
  'ap-northeast-1',
  'ap-southeast-1',
  'ap-southeast-2',
];

export function AccountForm({ onSubmit, onCancel }: AccountFormProps) {
  const [accountId, setAccountId] = useState('');
  const [accountName, setAccountName] = useState('');
  const [roleArn, setRoleArn] = useState('');
  const [externalId, setExternalId] = useState('');
  const [region, setRegion] = useState('us-east-1');
  const [tpmLimit, setTpmLimit] = useState('');
  const [rpmLimit, setRpmLimit] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAccountIdChange = (value: string) => {
    // Only allow digits, max 12
    const digits = value.replace(/\D/g, '').slice(0, 12);
    setAccountId(digits);

    // Auto-fill role ARN
    if (digits.length === 12) {
      setRoleArn(`arn:aws:iam::${digits}:role/flux-llmgw-bedrock-access`);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    if (!accountId.trim() || !accountName.trim() || !roleArn.trim() || !region) {
      setError('Account ID, Account Name, Role ARN, and Region are required.');
      setSubmitting(false);
      return;
    }

    if (!/^\d{12}$/.test(accountId)) {
      setError('Account ID must be exactly 12 digits.');
      setSubmitting(false);
      return;
    }

    if (!/^arn:aws:iam::\d{12}:role\//.test(roleArn)) {
      setError('Role ARN must be a valid IAM role ARN.');
      setSubmitting(false);
      return;
    }

    try {
      const data: CreateAccountData = {
        accountId: accountId.trim(),
        accountName: accountName.trim(),
        roleArn: roleArn.trim(),
        region,
      };
      if (externalId.trim()) data.externalId = externalId.trim();
      if (tpmLimit) data.tpmLimit = parseInt(tpmLimit, 10);
      if (rpmLimit) data.rpmLimit = parseInt(rpmLimit, 10);

      await onSubmit(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add account');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <div className="alert alert--error" style={{ marginBottom: 16 }}>
          <span>{error}</span>
        </div>
      )}

      <div className="modal__field">
        <label htmlFor="account-id">AWS Account ID (12 digits)</label>
        <input
          id="account-id"
          type="text"
          value={accountId}
          onChange={(e) => handleAccountIdChange(e.target.value)}
          placeholder="123456789012"
          required
          autoFocus
          maxLength={12}
        />
        <small className="modal__hint">
          The 12-digit AWS account ID for the remote Bedrock account.
        </small>
      </div>

      <div className="modal__field">
        <label htmlFor="account-name">Account Name</label>
        <input
          id="account-name"
          type="text"
          value={accountName}
          onChange={(e) => setAccountName(e.target.value)}
          placeholder="e.g. Production Bedrock (US East)"
          required
        />
        <small className="modal__hint">
          A human-friendly label for this account.
        </small>
      </div>

      <div className="modal__field">
        <label htmlFor="role-arn">IAM Role ARN</label>
        <input
          id="role-arn"
          type="text"
          value={roleArn}
          onChange={(e) => setRoleArn(e.target.value)}
          placeholder="arn:aws:iam::123456789012:role/flux-llmgw-bedrock-access"
          required
        />
        <small className="modal__hint">
          The cross-account IAM role ARN. Auto-filled when you enter a valid Account ID.
        </small>
      </div>

      <div className="modal__field">
        <label htmlFor="external-id">External ID (optional)</label>
        <input
          id="external-id"
          type="text"
          value={externalId}
          onChange={(e) => setExternalId(e.target.value)}
          placeholder="Optional external ID for STS AssumeRole"
        />
        <small className="modal__hint">
          If the cross-account role requires an external ID for the trust policy.
        </small>
      </div>

      <div className="modal__field">
        <label htmlFor="region">Region</label>
        <select
          id="region"
          value={region}
          onChange={(e) => setRegion(e.target.value)}
          required
        >
          {REGIONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <small className="modal__hint">
          The AWS region where Bedrock models will be invoked.
        </small>
      </div>

      <div className="modal__field">
        <label htmlFor="tpm-limit">TPM Limit (optional)</label>
        <input
          id="tpm-limit"
          type="number"
          value={tpmLimit}
          onChange={(e) => setTpmLimit(e.target.value)}
          placeholder="e.g. 1000000"
          min={0}
        />
        <small className="modal__hint">
          Tokens per minute limit for this account.
        </small>
      </div>

      <div className="modal__field">
        <label htmlFor="rpm-limit">RPM Limit (optional)</label>
        <input
          id="rpm-limit"
          type="number"
          value={rpmLimit}
          onChange={(e) => setRpmLimit(e.target.value)}
          placeholder="e.g. 1000"
          min={0}
        />
        <small className="modal__hint">
          Requests per minute limit for this account.
        </small>
      </div>

      <div className="modal__actions">
        <button type="button" className="modal__cancel" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="modal__submit" disabled={submitting}>
          {submitting ? 'Adding...' : 'Add Account'}
        </button>
      </div>
    </form>
  );
}
